<template>
  <div class="index-fee-statistics">
    <div class="card-header">
      <div class="header-accent"></div>
      <div class="header-content">
        <div class="header-icon">
          <i class="el-icon-wallet"></i>
        </div>
        <div class="header-title">
          <span>{{ $t('propertyIndex.feeStatistics') }}</span>
          <div class="header-subtitle">{{ $t('propertyIndex.feeStatisticsDesc') }}</div>
        </div>
      </div>
      <div class="header-extra">
        <el-date-picker v-model="curMonth" type="month" :clearable="false"
          value-format="yyyy-MM" size="small" style="width: 110px"
          @change="_changeMonth" />
      </div>
    </div>
    <div class="fee-grid" v-loading="loading">
      <div class="fee-block property" :title="$t('propertyIndex.enterRoomFee')" @click="_toRoomFee">
        <div class="fee-block-header">
          <div class="fee-type-icon">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="fee-type-name">{{ $t('propertyIndex.propertyFee') }}</div>
          <div class="fee-rate" @click.stop>
            <el-tooltip placement="top" effect="dark">
              <div slot="content">
                {{ $t('propertyIndex.rateTip') }}<br />
                {{ $t('propertyIndex.rateTipNumerator') }}<br />
                {{ $t('propertyIndex.rateTipDenominator') }}<br />
                {{ $t('propertyIndex.rateTipNote') }}
              </div>
              <div class="rate-value">
                {{ propertyRate }}<span class="rate-unit">%</span>
                <i class="el-icon-info rate-info"></i>
              </div>
            </el-tooltip>
            <div class="rate-label">{{ $t('propertyIndex.collectionRate') }}</div>
          </div>
          <div class="fee-arrow">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <div class="fee-metrics">
          <div class="metric-item">
            <div class="metric-value">¥ {{ _formatAmount(feeData.property.curReceivableFee) }}</div>
            <div class="metric-label">{{ $t('propertyIndex.receivable') }}</div>
          </div>
          <div class="metric-item">
            <div class="metric-value">¥ {{ _formatAmount(feeData.property.monthReceivedRoomAmount) }}</div>
            <div class="metric-label">{{ $t('propertyIndex.received') }}</div>
          </div>
          <div class="metric-item">
            <div class="metric-value owe">¥ {{ _formatAmount(feeData.property.oweFee) }}</div>
            <div class="metric-label">{{ $t('propertyIndex.oweFee') }}</div>
          </div>
          <div class="metric-item">
            <el-tooltip placement="top" effect="dark">
              <div slot="content">{{ $t('propertyIndex.receivedRoomsTip') }}</div>
              <div class="metric-value">
                {{ feeData.property.monthReceivedRoomCount }}/{{ feeData.property.feeRoomCount }}
                <span class="metric-unit">{{ $t('propertyIndex.household') }}</span>
              </div>
            </el-tooltip>
            <div class="metric-label">{{ $t('propertyIndex.receivedRooms') }}</div>
          </div>
        </div>
        <div class="fee-footer">
          <span>{{ $t('propertyIndex.enterRoomFee') }}</span>
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>

      <div class="fee-block parking" :title="$t('propertyIndex.enterCarFee')" @click="_toCarFee">
        <div class="fee-block-header">
          <div class="fee-type-icon">
            <i class="el-icon-truck"></i>
          </div>
          <div class="fee-type-name">{{ $t('propertyIndex.parkingFee') }}</div>
          <div class="fee-rate" @click.stop>
            <el-tooltip placement="top" effect="dark">
              <div slot="content">
                {{ $t('propertyIndex.rateTip') }}<br />
                {{ $t('propertyIndex.rateTipNumerator') }}<br />
                {{ $t('propertyIndex.rateTipDenominator') }}<br />
                {{ $t('propertyIndex.rateTipNote') }}
              </div>
              <div class="rate-value">
                {{ parkingRate }}<span class="rate-unit">%</span>
                <i class="el-icon-info rate-info"></i>
              </div>
            </el-tooltip>
            <div class="rate-label">{{ $t('propertyIndex.collectionRate') }}</div>
          </div>
          <div class="fee-arrow">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <div class="fee-metrics">
          <div class="metric-item">
            <div class="metric-value">¥ {{ _formatAmount(feeData.parking.curReceivableFee) }}</div>
            <div class="metric-label">{{ $t('propertyIndex.receivable') }}</div>
          </div>
          <div class="metric-item">
            <div class="metric-value">¥ {{ _formatAmount(feeData.parking.monthReceivedRoomAmount) }}</div>
            <div class="metric-label">{{ $t('propertyIndex.received') }}</div>
          </div>
          <div class="metric-item">
            <div class="metric-value owe">¥ {{ _formatAmount(feeData.parking.oweFee) }}</div>
            <div class="metric-label">{{ $t('propertyIndex.oweFee') }}</div>
          </div>
          <div class="metric-item">
            <el-tooltip placement="top" effect="dark">
              <div slot="content">{{ $t('propertyIndex.receivedRoomsTip') }}</div>
              <div class="metric-value">
                {{ feeData.parking.monthReceivedRoomCount }}/{{ feeData.parking.feeRoomCount }}
                <span class="metric-unit">{{ $t('propertyIndex.household') }}</span>
              </div>
            </el-tooltip>
            <div class="metric-label">{{ $t('propertyIndex.receivedRooms') }}</div>
          </div>
        </div>
        <div class="fee-footer">
          <span>{{ $t('propertyIndex.enterCarFee') }}</span>
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { queryFeeCollectionStatistics } from '@/api/report/dataReportApi'

// 费用类型编码：物业费 / 停车费(字典 pay_fee_config.fee_type_cd_show)
const FEE_TYPE_PROPERTY = '888800010001'
const FEE_TYPE_CAR = '888800010008'

function emptyFee() {
  return {
    curReceivableFee: '0.00',
    monthReceivedRoomAmount: '0.00',
    monthReceivedRoomCount: 0,
    feeRoomCount: 0,
    oweFee: '0.00'
  }
}

export default {
  name: 'IndexFeeStatistics',
  data() {
    return {
      curMonth: '',
      loading: false,
      feeData: {
        property: emptyFee(),
        parking: emptyFee()
      }
    }
  },
  computed: {
    propertyRate() {
      return this._computeRate(this.feeData.property)
    },
    parkingRate() {
      return this._computeRate(this.feeData.parking)
    }
  },
  created() {
    this.curMonth = this._formatMonth(new Date())
  },
  methods: {
    _loadFeeStatistics() {
      if (!this.curMonth) {
        return
      }
      const range = this._monthRange(this.curMonth)
      const param = {
        communityId: this.getCommunityId(),
        startDate: range.startDate,
        endDate: range.endDate
      }
      this.loading = true
      queryFeeCollectionStatistics(param)
        .then(res => {
          if (res.code === 0 && Array.isArray(res.data)) {
            const property = emptyFee()
            const parking = emptyFee()
            res.data.forEach(item => {
              if (item.feeTypeCd === FEE_TYPE_PROPERTY) {
                Object.assign(property, item)
              } else if (item.feeTypeCd === FEE_TYPE_CAR) {
                Object.assign(parking, item)
              }
            })
            // 整体替换，保证 Vue2 响应式
            this.feeData = { property, parking }
          }
        })
        .catch(err => {
          console.error('加载收缴统计失败', err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    _changeMonth() {
      this._loadFeeStatistics()
    },
    _computeRate(fee) {
      const received = Number(fee.monthReceivedRoomAmount) || 0
      const receivable = Number(fee.curReceivableFee) || 0
      if (receivable <= 0) {
        return '0.00'
      }
      return ((received / receivable) * 100).toFixed(2)
    },
    _formatAmount(value) {
      return (Number(value) || 0).toFixed(2)
    },
    _formatMonth(date) {
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0')
    },
    _monthRange(month) {
      const parts = month.split('-')
      const year = Number(parts[0])
      const monthIndex = Number(parts[1]) - 1
      return {
        startDate: this._formatDate(new Date(year, monthIndex, 1)),
        endDate: this._formatDate(new Date(year, monthIndex + 1, 0))
      }
    },
    _formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    },
    _toRoomFee() {
      this.$router.push('/pages/property/roomCreateFee')
    },
    _toCarFee() {
      this.$router.push('/pages/property/carCreateFee')
    }
  }
}
</script>

<style lang="scss" scoped>
.index-fee-statistics {
  background: #fff;
  border-radius: 4px;
  padding: 15px;
  overflow: hidden;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .card-header {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    padding: 0 10px;

    .header-accent {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #E6A23C, #f7ba2a);
      border-radius: 2px;
    }

    .header-content {
      display: flex;
      align-items: center;
      margin-left: 10px;

      .header-icon {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #E6A23C 0%, #f7ba2a 100%);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        box-shadow: 0 4px 8px rgba(230, 162, 60, 0.3);

        i {
          font-size: 24px;
          color: #fff;
        }
      }

      .header-title {
        span {
          font-size: 18px;
          font-weight: bold;
          color: #303133;
          display: block;
        }

        .header-subtitle {
          font-size: 12px;
          color: #909399;
          margin-top: 4px;
        }
      }
    }

    .header-extra {
      margin-left: auto;
    }
  }

  .fee-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    min-height: 150px;

    .fee-block {
      position: relative;
      padding: 15px 18px;
      background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
      border: 1px solid #f0f0f0;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 3px;
      }

      &.property::before {
        background: linear-gradient(90deg, #409EFF, #1890ff);
      }

      &.parking::before {
        background: linear-gradient(90deg, #67C23A, #85ce61);
      }

      &.property .fee-type-icon {
        background: linear-gradient(135deg, #409EFF 0%, #1890ff 100%);
      }

      &.parking .fee-type-icon {
        background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

        .fee-arrow {
          opacity: 1;
          transform: translateX(0);
        }
      }

      &.property:hover {
        border-color: #409EFF;
      }

      &.parking:hover {
        border-color: #67C23A;
      }

      .fee-block-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        .fee-type-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 10px;

          i {
            font-size: 20px;
            color: #fff;
          }
        }

        .fee-type-name {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
        }

        .fee-rate {
          margin-left: auto;
          text-align: right;

          .rate-value {
            font-size: 22px;
            font-weight: bold;
            color: #303133;
            line-height: 1.2;

            .rate-unit {
              font-size: 13px;
              margin-left: 1px;
            }

            .rate-info {
              font-size: 13px;
              color: #c0c4cc;
              margin-left: 3px;
            }
          }

          .rate-label {
            font-size: 12px;
            color: #909399;
          }
        }

        .fee-arrow {
          margin-left: 12px;
          opacity: 0.5;
          transform: translateX(-8px);
          transition: all 0.3s;

          i {
            font-size: 16px;
            color: #909399;
          }
        }
      }

      .fee-metrics {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid #f0f0f0;

        .metric-value {
          font-size: 17px;
          font-weight: bold;
          color: #303133;

          &.owe {
            color: #F56C6C;
          }

          .metric-unit {
            font-size: 12px;
            color: #909399;
            margin-left: 2px;
          }
        }

        .metric-label {
          font-size: 12px;
          color: #909399;
          margin-top: 4px;
        }
      }

      .fee-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-top: 8px;
        font-size: 12px;
        color: #c0c4cc;

        i {
          font-size: 12px;
          margin-left: 2px;
        }
      }
    }
  }
}
</style>
